import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import ModalSidebarNav from './ModalSidebarNav';
import useSetupProgress from '../hooks/useSetupProgress';
import {
  Rocket,
  Check,
  ArrowRight,
  Sparkles,
  Shield,
  Users,
  Package,
  Zap,
  FileText,
  CreditCard,
  Link2,
  Store,
  PenTool,
  LayoutDashboard,
  Hand,
  LifeBuoy,
} from 'lucide-react';
import { SectionHeader, Card, TAB_HELP, ADMIN_EXTRA_HELP, TEAM_EXTRA_HELP } from './help/helpSections';

/* ─────────────────────────────────────────────────────────────────────────
 * Admin: Setup home (only shown to admins/owners)
 * ────────────────────────────────────────────────────────────────────── */
const SetupHome = ({ progress, onNavigate, onClose }) => {
  const { completedCore, totalCore, progressPercent, hasStripeConnect, stripeIsLive } = progress;
  const allDone = completedCore === totalCore;

  const go = (view) => {
    onNavigate?.(view);
    onClose?.();
  };

  const phases = [
    {
      name: 'Build your business',
      steps: [
        { id: 'asset', title: 'Register an asset', desc: 'Boats, venues, vehicles — whatever you book against.', icon: Package, complete: progress.hasAsset, to: 'assets' },
        { id: 'role', title: 'Create a role', desc: 'Define positions like Captain, Guide, or Instructor.', icon: Shield, complete: progress.hasRole, to: 'roles' },
        { id: 'team', title: 'Add a team member', desc: 'Add yourself or staff — they get their own logins.', icon: Users, complete: progress.hasTeamMember, to: 'teamMembers' },
        { id: 'service', title: 'Create a service', desc: 'Build the bookable experience your customers buy.', icon: Zap, complete: progress.hasService, to: 'services' },
      ],
    },
    {
      name: 'Connect payments',
      steps: [
        { id: 'waiver', title: 'Make your waiver decision', desc: 'Add waiver text customers sign — or sign off on running without one.', icon: FileText, complete: progress.hasWaiverText, to: 'waivers' },
        { id: 'stripe', title: 'Connect a payment processor', desc: progress.hasPaymentProcessor ? `✓ ${progress.processorName} connected — payments go straight to you.` : 'Stripe or Ziina — payments go straight to you, we never hold funds.', icon: CreditCard, complete: progress.hasPaymentProcessor, to: 'payments' },
      ],
    },
    {
      name: 'Take bookings',
      steps: [
        { id: 'flow', title: 'Create a booking flow', desc: 'Embed on your site, share a link, or QR it on signage.', icon: Link2, complete: progress.hasFlow, to: 'flows' },
      ],
    },
  ];

  return (
    <div>
      <SectionHeader
        icon={Rocket}
        title="Setup"
        subtitle={allDone ? "You're all set. Optional steps below." : 'Finish setup to start taking real bookings.'}
      />

      {/* Progress card */}
      <div className="rounded-2xl bg-gray-900 text-white p-6 mb-7 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="relative">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-1">Setup progress</div>
              <div className="text-3xl font-semibold tracking-tight">
                {completedCore}<span className="text-gray-500 text-xl"> / {totalCore}</span>
              </div>
            </div>
            <div className="text-sm text-gray-400">{progressPercent}%</div>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-400 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
              data-testid="setup-progress-bar"
            />
          </div>
          {hasStripeConnect && !stripeIsLive && progress.processorName !== 'Ziina' && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-amber-300">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Stripe is in test mode — switch to live when you're ready.
            </div>
          )}
        </div>
      </div>

      <div className="space-y-7">
        {phases.map((phase) => (
          <div key={phase.name}>
            <div className="text-xs uppercase tracking-[0.14em] text-gray-500 font-medium mb-2.5">{phase.name}</div>
            <div className="space-y-1.5">
              {phase.steps.map((step) => {
                const Icon = step.icon;
                return (
                  <button
                    key={step.id}
                    onClick={() => go(step.to)}
                    data-testid={`help-setup-step-${step.id}`}
                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left transition-all group ${
                      step.complete
                        ? 'bg-emerald-50/60 hover:bg-emerald-50'
                        : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      step.complete ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700 group-hover:bg-gray-900 group-hover:text-white transition-colors'
                    }`}>
                      {step.complete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${step.complete ? 'text-emerald-900' : 'text-gray-900'}`}>{step.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">{step.desc}</div>
                    </div>
                    <ArrowRight className={`w-4 h-4 flex-shrink-0 transition-all ${
                      step.complete ? 'text-emerald-600' : 'text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5'
                    }`} />
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-gray-500 font-medium mb-2.5">Optional — when you're ready</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <button onClick={() => go('marketplace')} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group" data-testid="help-setup-marketplace">
              <Store className="w-4 h-4 text-gray-700 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">List on Marketplace</div>
                <div className="text-xs text-gray-500">Public profile + listing</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5 transition-all" />
            </button>
            <button onClick={() => go('blog')} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group" data-testid="help-setup-blog">
              <PenTool className="w-4 h-4 text-gray-700 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">Generate SEO content</div>
                <div className="text-xs text-gray-500">AI blog posts for visibility</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
 * Team-member welcome — when admin setup home isn't relevant
 * ────────────────────────────────────────────────────────────────────── */
const WelcomeHome = ({ availableTabs, onNavigate, onClose, displayName }) => {
  const go = (view) => {
    onNavigate?.(view);
    onClose?.();
  };

  return (
    <div>
      <SectionHeader
        icon={Hand}
        title={`Welcome${displayName ? `, ${displayName}` : ''}`}
        subtitle="Quick orientation to the tabs you have access to."
      />

      <Card>
        <p className="text-sm text-gray-600 leading-relaxed">
          Your admin set up your access. Below is everything you can see — tap any of
          them to jump straight in, or pick a section in the sidebar for a deeper guide.
        </p>
      </Card>

      <div className="mt-3 flex items-start gap-3 rounded-xl border border-[#1F2A5C]/15 bg-[#1F2A5C]/[0.04] p-4">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#F5B92E]" />
        <p className="text-sm text-gray-700 leading-relaxed">
          <strong className="text-gray-900">New here? Take the guided tour.</strong> Click the
          ✨ sparkle tab on the right edge of the screen anytime to replay the Basic tour,
          start the Advanced (data &amp; marketing) tour, or reopen these docs.
        </p>
      </div>

      <div className="mt-6 space-y-1.5">
        <div className="text-xs uppercase tracking-[0.14em] text-gray-500 font-medium mb-2">Your tabs</div>
        {availableTabs.length === 0 && (
          <Card>
            <p className="text-sm text-gray-500">
              No tabs assigned yet. Ask your admin to grant access from the Roles tab.
            </p>
          </Card>
        )}
        {availableTabs.map((t) => {
          const Icon = t.icon || LayoutDashboard;
          return (
            <button
              key={t.id}
              onClick={() => go(t.navId || t.id)}
              data-testid={`help-welcome-tab-${t.id}`}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group bg-white"
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gray-100 text-gray-700 group-hover:bg-gray-900 group-hover:text-white transition-colors flex-shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{t.label}</div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">{t.blurb}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
 * SupportHome — "Need support?" section linking to the Help Center
 * ────────────────────────────────────────────────────────────────────── */

const SupportHome = () => (
  <div data-testid="help-support-section">
    <div className="mb-6">
      <h2 className="text-xl font-bold text-gray-900">Need support?</h2>
      <p className="mt-1 text-sm text-gray-500">
        Search the BookingFlows Help Center — refunds, waivers, check-in, roles, financials and more.
        Can&rsquo;t find your answer? Submit a support ticket and our team is notified instantly.
      </p>
    </div>
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#1F2A5C] text-[#F5B92E]">
          <LifeBuoy className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">BookingFlows Help Center</p>
          <p className="mt-1 text-xs text-gray-500">
            Searchable articles for every core feature, plus a support ticket system for anything we haven&rsquo;t covered.
          </p>
          <a
            href="/support"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="help-open-help-center"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1F2A5C] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#28356f]"
          >
            Open the Help Center <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
 * HelpPage — role-aware modal
 * ────────────────────────────────────────────────────────────────────── */

const HelpPage = ({
  isOpen,
  onClose,
  initialSection = null,
  onNavigate,
  isAdmin = true,
  navGroups = [],
}) => {
  const progress = useSetupProgress({ enabled: isAdmin });

  // Build the list of dashboard tab IDs available to this user.
  const availableNavIds = useMemo(() => {
    const ids = [];
    navGroups.forEach((g) => {
      if (g.isStandalone) ids.push(g.id);
      else if (g.items) g.items.forEach((it) => ids.push(it.id));
    });
    return ids;
  }, [navGroups]);

  // Resolve which content tabs the help guide should expose.
  // - Admins always see ALL tab help (Setup as home, every section)
  // - Team members see only help for the tabs they actually have access to,
  //   plus a Welcome home, Ask Bosun docs, and a My Profile section.
  const sections = useMemo(() => {
    if (isAdmin) {
      const all = {};
      // Setup goes first
      all.setup = { label: 'Setup', icon: Rocket, isHome: true };
      // Walk the dashboard nav order so help mirrors the sidebar
      navGroups.forEach((g) => {
        const collect = (id) => {
          if (TAB_HELP[id]) all[id] = TAB_HELP[id];
        };
        if (g.isStandalone) collect(g.id);
        else if (g.items) g.items.forEach((it) => collect(it.id));
      });
      // Intelligence lives outside the tab nav — always document it for admins
      if (!all.intelligence) all.intelligence = TAB_HELP.intelligence;
      // Admin-only extra help (Ask Bosun & Tours, Settings)
      Object.entries(ADMIN_EXTRA_HELP).forEach(([k, v]) => {
        all[k] = v;
      });
      return all;
    }

    // Team member view — welcome home, then mirror their actual tabs
    const tm = {};
    tm.welcome = { label: 'Welcome', icon: Hand, isHome: true };
    availableNavIds.forEach((id) => {
      if (TAB_HELP[id]) tm[id] = TAB_HELP[id];
    });
    Object.entries(TEAM_EXTRA_HELP).forEach(([k, v]) => {
      tm[k] = v;
    });
    return tm;
  }, [isAdmin, navGroups, availableNavIds]);

  const homeId = isAdmin ? 'setup' : 'welcome';
  const [activeSection, setActiveSection] = useState(initialSection || homeId);

  // Keep section in range if the user's permissions change between opens
  useEffect(() => {
    if (!sections[activeSection]) {
      setActiveSection(homeId);
    }
  }, [sections, activeSection, homeId]);

  useEffect(() => {
    if (initialSection && initialSection !== activeSection && sections[initialSection]) {
      setActiveSection(initialSection);
    }
    // React only to parent-driven section changes
  }, [initialSection]);

  const tabsList = Object.entries(sections).map(([id, def]) => ({
    id,
    label: def.label,
    icon: def.icon,
  }));

  // For team-member welcome page, build the tile list of their tabs
  const welcomeTabs = useMemo(() => {
    return availableNavIds
      .filter((id) => TAB_HELP[id])
      .map((id) => ({ id, navId: id, ...TAB_HELP[id] }));
  }, [availableNavIds]);

  const renderSection = () => {
    if (activeSection === 'support') {
      return <SupportHome />;
    }
    if (activeSection === 'setup' && isAdmin) {
      return <SetupHome progress={progress} onNavigate={onNavigate} onClose={onClose} />;
    }
    if (activeSection === 'welcome' && !isAdmin) {
      return (
        <WelcomeHome
          availableTabs={welcomeTabs}
          onNavigate={onNavigate}
          onClose={onClose}
        />
      );
    }
    const def = sections[activeSection];
    const Component = def?.component;
    if (Component) return <Component />;
    // Fallback to home
    return isAdmin ? (
      <SetupHome progress={progress} onNavigate={onNavigate} onClose={onClose} />
    ) : (
      <WelcomeHome availableTabs={welcomeTabs} onNavigate={onNavigate} onClose={onClose} />
    );
  };

  // Setup-tab badge for admins (only)
  const tabsForSidebar = tabsList.map((t) =>
    t.id === 'setup' && isAdmin && progress.remainingCore > 0
      ? { ...t, badge: progress.remainingCore }
      : t
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-5xl h-[85vh] overflow-hidden flex flex-col p-0 sm:rounded-xl"
        data-testid="help-modal"
      >
        <DialogTitle className="sr-only">Help</DialogTitle>
        <DialogDescription className="sr-only">Setup guide and feature documentation</DialogDescription>
        {/* Mobile: horizontal scrollable tabs */}
        <div className="md:hidden bg-gray-50 overflow-x-auto flex-shrink-0">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Help</h2>
            {isAdmin && progress.remainingCore > 0 && (
              <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-blue-600 text-white">
                {progress.remainingCore} to set up
              </span>
            )}
          </div>
          <div className="flex gap-1.5 p-2 min-w-max">
            {tabsList.map((tab) => {
              const isActive = activeSection === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 bg-white'
                  }`}
                  data-testid={`help-tab-mobile-${tab.id}`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Desktop: sidebar + content — no right border, matches Settings modal */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          <ModalSidebarNav
            title="Help"
            tabs={tabsForSidebar}
            activeTab={activeSection}
            onTabChange={setActiveSection}
          />
          <div className="flex-1 overflow-y-auto min-w-0 bg-white">
            <div className="p-6 sm:p-10 max-w-3xl">{renderSection()}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpPage;
